import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  addOutline,
  albumsOutline,
  arrowRedoOutline,
  arrowUndoOutline,
  chatbubblesOutline,
  chevronForwardOutline,
  closeOutline,
  colorPaletteOutline,
  copyOutline,
  cubeOutline,
  desktopOutline,
  documentTextOutline,
  eyeOutline,
  gridOutline,
  imageOutline,
  layersOutline,
  lockClosedOutline,
  megaphoneOutline,
  moonOutline,
  openOutline,
  phonePortraitOutline,
  pricetagOutline,
  radioButtonOnOutline,
  saveOutline,
  scanOutline,
  sparklesOutline,
  sunnyOutline,
  tabletPortraitOutline,
  terminalOutline,
  textOutline
} from 'ionicons/icons';
import { firstValueFrom, startWith } from 'rxjs';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { AppContextMenuItem } from '../../components/app-context-menu/app-context-menu.models';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { PendingBuilderSaveAware } from '../../guards/pending-builder-save.guard';
import {
  CANVAS_THEME_FIELDS,
  CanvasThemeMode,
  CanvasThemePackage
} from '../../models/component-canvas-theme.models';
import {
  WEBSITE_BUILDER_SECTION_PRESETS,
  WebsitePageDocument,
  WebsiteBuilderNode,
  WebsiteBuilderNodeType,
  getWebsiteBuilderNodeIcon,
  isWebsiteBuilderContainerNode,
  websiteBuilderDropListId
} from '../../models/website-builder.models';
import { WebsitePageSummary } from '../../models/website-management.models';
import { AppContextMenuService } from '../../services/app-context-menu.service';
import { ComponentCanvasThemeService } from '../../services/component-canvas-theme.service';
import { ComponentStudioService } from '../../services/component-studio.service';
import { WebsiteBuilderApiService } from '../../services/website-builder-api.service';
import { WebsiteBuilderService } from '../../services/website-builder.service';
import { WebsiteManagementService } from '../../services/website-management.service';
import { AuthService } from '../../services/auth.service';
import { mapCanvasThemeDefaults } from '../../utils/canvas-theme-style.utils';
import { isUuidV7 } from '../../utils/public-id.utils';
import {
  AdaptiveGridThemeMode,
  AdaptiveViewportGridContext,
  AdaptiveViewportGridRenderer
} from '../../utils/adaptive-viewport-grid-renderer';
import { ViewportRulerRenderer, ViewportRulerRendererContext } from '../../utils/viewport-ruler-renderer';
import { buildPublishedWebsiteHost, buildPublishedWebsiteUrl } from '../../utils/website-runtime-url.utils';
import { environment } from '../../../environments/environment';

type PreviewMode = 'responsive' | 'mobile-s' | 'mobile-m' | 'mobile-l' | 'tablet' | 'laptop' | 'laptop-lg' | 'desktop' | '4k';
type ComponentSourceFilter = 'all' | 'built-in' | 'custom';
type NodeResizeDirection = 'n' | 'e' | 's' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
type ViewportResizeDirection = 'e' | 's' | 'w' | 'se' | 'sw';

interface PreviewPreset {
  id: PreviewMode;
  label: string;
  shortLabel: string;
  icon: string;
  width: number;
  height: number;
}

interface NodeManipulationState {
  kind: 'move' | 'resize';
  nodeId: string;
  direction?: NodeResizeDirection;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  startOffsetX: number;
  startOffsetY: number;
  startCenterX: number;
  startCenterY: number;
  parentWidth: number;
  parentHeight: number;
}

@Component({
  selector: 'app-website-builder-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    IonIcon,
    IonSpinner,
    RouterLink,
    ApiLoadErrorComponent,
    InfoTooltipComponent
  ],
  templateUrl: './website-builder-page.component.html',
  styleUrls: ['./website-builder-page.component.scss']
})
export class WebsiteBuilderPageComponent implements OnInit, AfterViewInit, PendingBuilderSaveAware {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly canvasViewportStorageKey = 'bw.website-builder.viewport';
  private readonly defaultCanvasWidth = 1280;
  private readonly defaultCanvasHeight = 860;
  private readonly minCanvasWidth = 1;
  private readonly maxCanvasWidth = 100000;
  private readonly minCanvasHeight = 1;
  private readonly maxCanvasHeight = 100000;
  private readonly canvasViewportFitPaddingX = 4;
  private readonly canvasViewportFitPaddingY = 16;
  private readonly canvasViewportFrameChromeWidthRem = 1.7;
  private readonly canvasViewportFrameChromeHeightRem = 0.85;
  private readonly canvasViewportHandleOverhangWidthRem = 0.96;
  private readonly canvasViewportHandleOverhangHeightRem = 0.96;
  private readonly canvasViewportRulerThicknessRem = 1;

  @ViewChild('canvasViewportShell') private canvasViewportShellRef?: ElementRef<HTMLDivElement>;

  readonly builderService = this.websiteBuilderService;
  readonly componentStudioService = this.componentStudioServiceInstance;
  readonly canvasThemeService = this.componentCanvasThemeService;
  readonly canvasThemeFields = CANVAS_THEME_FIELDS;
  readonly previewPresets: PreviewPreset[] = [
    { id: 'mobile-s', label: 'Mobile S', shortLabel: 'S', icon: 'phone-portrait-outline', width: 320, height: 568 },
    { id: 'mobile-m', label: 'Mobile M', shortLabel: 'M', icon: 'phone-portrait-outline', width: 375, height: 667 },
    { id: 'mobile-l', label: 'Mobile L', shortLabel: 'L', icon: 'phone-portrait-outline', width: 430, height: 932 },
    { id: 'tablet', label: 'Tablet', shortLabel: 'Tab', icon: 'tablet-portrait-outline', width: 834, height: 1112 },
    { id: 'laptop', label: 'Laptop', shortLabel: 'Lap', icon: 'desktop-outline', width: 1280, height: 800 },
    { id: 'laptop-lg', label: 'Laptop large', shortLabel: 'L+', icon: 'desktop-outline', width: 1440, height: 900 },
    { id: 'desktop', label: 'Desktop', shortLabel: 'Desk', icon: 'desktop-outline', width: 1600, height: 960 },
    { id: '4k', label: '4K', shortLabel: '4K', icon: 'desktop-outline', width: 2560, height: 1440 }
  ];
  readonly sectionPresets = WEBSITE_BUILDER_SECTION_PRESETS;
  readonly zoomPresetOptions = [50, 75, 90, 100, 110, 125, 150, 175, 200];
  isLoading = true;
  loadError: string | null = null;
  componentLibraryLoading = false;
  componentLibraryError = '';
  pageMessage = '';
  isSavingWebsiteVersion = false;
  previewMode: PreviewMode = 'laptop';
  canvasWidth = this.defaultCanvasWidth;
  canvasHeight = this.defaultCanvasHeight;
  canvasFitScale = 1;
  canvasScale = 1;
  canvasZoomPercentage = 100;
  canvasViewportStyle: Record<string, string> = {};
  canvasViewportFrameStyle: Record<string, string> = {};
  scaledCanvasViewportStyle: Record<string, string> = {};
  gridStyle: Record<string, string> = {};
  topRulerCanvasStyle: Record<string, string> = {};
  leftRulerCanvasStyle: Record<string, string> = {};
  showViewportGrid = false;
  showViewportRulers = false;
  canvasViewportScrollLeft = 0;
  canvasViewportScrollTop = 0;
  showZoomPresetMenu = false;
  showPagesDrawer = false;
  showLayersDrawer = false;
  showSectionModal = false;
  showComponentModal = false;
  showThemeManagerModal = false;
  showPreviewModal = false;
  readonly pagesPanelSections = {
    current: true,
    all: true
  };
  readonly layersPanelSections = {
    selection: true,
    tree: true
  };
  isPreviewLoading = false;
  previewError = '';
  isPublishing = false;
  componentSearchTerm = '';
  componentSourceFilter: ComponentSourceFilter = 'all';
  editingThemePackageId = 'default';
  themeDraft: CanvasThemePackage | null = null;
  previewPage: WebsitePageSummary | null = null;
  previewDocument: WebsitePageDocument | null = null;
  snapGuideX: number | null = null;
  snapGuideY: number | null = null;

  private resizingViewport = false;
  private viewportResizeDirection: ViewportResizeDirection = 'se';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeStartScale = 1;
  private requestedWebsitePublicId: string | null = null;
  private requestedPagePublicId: string | null = null;
  private loadCycle = 0;
  private loadSettlementTimer: ReturnType<typeof setTimeout> | null = null;
  private previewLoadCycle = 0;
  private readonly previewCache = new Map<string, { page: WebsitePageSummary; document: WebsitePageDocument }>();
  private nodeManipulationState: NodeManipulationState | null = null;
  private canvasViewportResizeObserver?: ResizeObserver;
  private canvasViewportScrollbarMeasureFrame: number | null = null;
  private canvasViewportScrollbarWidth = 0;
  private suppressSelectionUntil = 0;
  private preferredCanvasWidth = this.defaultCanvasWidth;
  private preferredCanvasHeight = this.defaultCanvasHeight;
  private canvasWidthFollowsAvailableSpace = false;
  private canvasHeightFollowsAvailableSpace = false;
  private autoFitPresetZoom = this.previewMode !== 'responsive';
  private readonly collapsedLayerNodeIds = new Set<string>();
  private readonly adaptiveGridRenderer = new AdaptiveViewportGridRenderer(() => this.getAdaptiveGridContext());
  private readonly viewportRulerRenderer = new ViewportRulerRenderer(() => this.getViewportRulerContext());

  constructor(
    private readonly websiteBuilderService: WebsiteBuilderService,
    private readonly websiteBuilderApiService: WebsiteBuilderApiService,
    private readonly componentCanvasThemeService: ComponentCanvasThemeService,
    private readonly componentStudioServiceInstance: ComponentStudioService,
    private readonly contextMenuService: AppContextMenuService,
    private readonly websiteManagementService: WebsiteManagementService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    addIcons({
      addOutline,
      albumsOutline,
      arrowRedoOutline,
      arrowUndoOutline,
      chatbubblesOutline,
      chevronForwardOutline,
      closeOutline,
      colorPaletteOutline,
      copyOutline,
      cubeOutline,
      desktopOutline,
      documentTextOutline,
      eyeOutline,
      gridOutline,
      imageOutline,
      layersOutline,
      lockClosedOutline,
      megaphoneOutline,
      moonOutline,
      openOutline,
      phonePortraitOutline,
      pricetagOutline,
      radioButtonOnOutline,
      saveOutline,
      scanOutline,
      sparklesOutline,
      sunnyOutline,
      tabletPortraitOutline,
      terminalOutline,
      textOutline
    });

    this.restoreStoredViewport();
    this.destroyRef.onDestroy(() => this.clearLoadSettlementTimer());
    this.destroyRef.onDestroy(() => this.canvasViewportResizeObserver?.disconnect());
    this.destroyRef.onDestroy(() => this.adaptiveGridRenderer.disconnect());
    this.destroyRef.onDestroy(() => this.viewportRulerRenderer.disconnect());
    this.destroyRef.onDestroy(() => {
      if (this.canvasViewportScrollbarMeasureFrame !== null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(this.canvasViewportScrollbarMeasureFrame);
      }
    });

    this.route.queryParamMap
      .pipe(startWith(this.route.snapshot.queryParamMap), takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params) => {
        const nextWebsitePublicId = this.parsePublicId(params.get('website'));
        const nextPagePublicId = this.parsePublicId(params.get('page'));
        const currentWebsitePublicId = this.activeWebsite?.publicId ?? null;
        const currentPagePublicId = this.activePage?.publicId ?? null;
        const contextChanged = !!currentWebsitePublicId && !!currentPagePublicId && (
          currentWebsitePublicId !== nextWebsitePublicId || currentPagePublicId !== nextPagePublicId
        );

        if (contextChanged) {
          await this.flushPendingChanges();
        }

        this.requestedWebsitePublicId = nextWebsitePublicId;
        this.requestedPagePublicId = nextPagePublicId;
        this.loadBuilderData();
      });
  }

  ngOnInit(): void {
    this.loadComponentLibrary();
    this.loadThemePackages();
  }

  ngAfterViewInit(): void {
    this.setupCanvasViewportObserver();
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  get connectedDropListIds(): string[] {
    return this.websiteBuilderService.dropListIds();
  }

  get canvasShellStyle(): Record<string, string> {
    return this.canvasThemeService.buildCanvasVariableStyle();
  }

  get currentCanvasThemePackageId(): string {
    return this.canvasThemeService.selectedPackageId();
  }

  get currentCanvasThemeMode(): CanvasThemeMode {
    return this.canvasThemeService.previewMode();
  }

  get hasPendingBuilderChanges(): boolean {
    return this.builderService.hasPendingChanges() || this.canvasThemeService.hasPendingChanges();
  }

  get isSavingBuilderState(): boolean {
    return this.builderService.isSavingDraft() || this.canvasThemeService.isSaving();
  }

  get activeWebsite() {
    return this.websiteBuilderService.website();
  }

  get activePage() {
    return this.websiteBuilderService.page();
  }

  get activeDocument() {
    return this.websiteBuilderService.document();
  }

  get selectedNode() {
    return this.websiteBuilderService.selectedNode();
  }

  get previewPages(): WebsitePageSummary[] {
    return this.websiteBuilderService.pages();
  }

  get currentPreviewPage(): WebsitePageSummary | null {
    return this.previewPage ?? this.activePage ?? null;
  }

  get currentPreviewDocument(): WebsitePageDocument | null {
    if (!this.currentPreviewPage) {
      return null;
    }

    if (this.currentPreviewPage.publicId === this.activePage?.publicId) {
      return this.activeDocument ?? null;
    }

    return this.previewDocument;
  }

  get insertionTargetLabel(): string {
    const selectedNode = this.selectedNode;
    if (selectedNode && this.isContainer(selectedNode)) {
      return selectedNode.label;
    }

    return this.websiteBuilderService.selectedParentNode()?.label ?? this.activeDocument?.root.label ?? 'Page root';
  }

  get showLeftSidebar(): boolean {
    return this.showPagesDrawer || this.showLayersDrawer;
  }

  get filteredComponentDefinitions() {
    const term = this.componentSearchTerm.trim().toLowerCase();
    const sourceFilter = this.componentSourceFilter;

    return this.componentStudioService.definitions()
      .filter((definition) => sourceFilter === 'all' || definition.source === sourceFilter)
      .filter((definition) => {
        if (!term) {
          return true;
        }

        return definition.name.toLowerCase().includes(term)
          || definition.description.toLowerCase().includes(term)
          || definition.tags.some((tag) => tag.toLowerCase().includes(term));
      });
  }

  loadBuilderData(force = false): void {
    if (!this.requestedWebsitePublicId || !this.requestedPagePublicId) {
      this.isLoading = false;
      this.loadError = 'Open a website and page from the Websites area before using the builder.';
      return;
    }

    const loadCycle = ++this.loadCycle;
    this.clearLoadSettlementTimer();
    this.isLoading = true;
    this.loadError = null;

    this.websiteBuilderService.loadBuilder(this.requestedWebsitePublicId, this.requestedPagePublicId, force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            this.scheduleLoadSettlement(loadCycle, () => {
              this.isLoading = false;
              queueMicrotask(() => {
                this.clampCanvasViewport();
                this.refreshCanvasViewportPresentation();
              });
            });
          });
        },
        error: () => {
          this.runInAngular(() => {
            this.scheduleLoadSettlement(loadCycle, () => {
              this.loadError = this.websiteBuilderService.loadError()
                || 'We could not load the website builder right now.';
              this.isLoading = false;
            });
          });
        }
      });
  }

  loadComponentLibrary(force = false): void {
    this.componentLibraryLoading = true;
    this.componentLibraryError = '';

    this.componentStudioService.loadState(force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            this.componentLibraryLoading = false;
          });
        },
        error: () => {
          this.runInAngular(() => {
            this.componentLibraryLoading = false;
            this.componentLibraryError = this.componentStudioService.loadError() || 'The component library could not be loaded.';
          });
        }
      });
  }

  loadThemePackages(force = false): void {
    this.canvasThemeService.loadPackages(force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runInAngular(() => {
            if (this.showThemeManagerModal) {
              this.loadThemeDraft(this.currentCanvasThemePackageId);
            }
          });
        },
        error: () => {
          // Canvas theme packages are non-blocking because the default theme always exists locally.
        }
      });
  }

  setPreviewMode(mode: PreviewMode): void {
    this.previewMode = mode;
    if (mode === 'responsive') {
      this.autoFitPresetZoom = false;
      this.canvasWidthFollowsAvailableSpace = false;
      this.canvasHeightFollowsAvailableSpace = false;
      this.persistViewport();
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
      return;
    }

    const preset = this.previewPresets.find((item) => item.id === mode);
    if (!preset) {
      return;
    }

    this.canvasWidthFollowsAvailableSpace = false;
    this.canvasHeightFollowsAvailableSpace = false;
    this.preferredCanvasWidth = clamp(preset.width, this.minCanvasWidth, this.maxCanvasWidth);
    this.autoFitPresetZoom = true;
    this.syncPresetZoomToAvailableWidth();
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  updateViewportWidth(value: string | number): void {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasWidthFollowsAvailableSpace = false;
    this.preferredCanvasWidth = clamp(Math.round(parsed), this.minCanvasWidth, this.maxCanvasWidth);
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  updateViewportHeight(value: string | number): void {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasHeightFollowsAvailableSpace = false;
    this.preferredCanvasHeight = clamp(Math.round(parsed), this.minCanvasHeight, this.maxCanvasHeight);
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  updateViewportZoom(value: string | number): void {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    this.autoFitPresetZoom = false;
    this.canvasZoomPercentage = clamp(Math.round(parsed), 5, 300);
    if (this.previewMode === 'responsive') {
      const bounds = this.getCanvasResizeBounds();
      this.preferredCanvasWidth = Math.min(this.preferredCanvasWidth, bounds.width);
      this.preferredCanvasHeight = Math.min(this.preferredCanvasHeight, bounds.height);
    }
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  nudgeViewportZoom(delta: number): void {
    this.updateViewportZoom(this.canvasZoomPercentage + delta);
  }

  selectViewportZoomPreset(value: string | number): void {
    this.updateViewportZoom(value);
    this.showZoomPresetMenu = false;
  }

  openViewportZoomMenu(): void {
    this.showZoomPresetMenu = true;
  }

  toggleViewportGrid(): void {
    this.showViewportGrid = !this.showViewportGrid;
    this.persistViewport();
    if (!this.showViewportGrid) {
      this.adaptiveGridRenderer.disconnect();
      return;
    }

    queueMicrotask(() => this.refreshCanvasViewportPresentation());
  }

  toggleViewportRulers(): void {
    this.showViewportRulers = !this.showViewportRulers;
    this.persistViewport();
    if (!this.showViewportRulers) {
      this.viewportRulerRenderer.disconnect();
      this.refreshCanvasViewportPresentation();
      return;
    }

    queueMicrotask(() => this.refreshCanvasViewportPresentation());
  }

  handleCanvasViewportScroll(event: Event): void {
    const viewport = event.target as HTMLDivElement | null;
    this.canvasViewportScrollLeft = viewport?.scrollLeft ?? 0;
    this.canvasViewportScrollTop = viewport?.scrollTop ?? 0;
    this.refreshCanvasChromePresentation();
  }

  maximizeViewportWidth(): void {
    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasWidthFollowsAvailableSpace = true;
    this.preferredCanvasWidth = this.getCanvasResizeBounds().width;
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  maximizeViewportHeight(): void {
    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    this.canvasHeightFollowsAvailableSpace = true;
    this.preferredCanvasHeight = this.getCanvasResizeBounds().height;
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  maximizeViewport(): void {
    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    const bounds = this.getCanvasResizeBounds();
    this.canvasWidthFollowsAvailableSpace = true;
    this.canvasHeightFollowsAvailableSpace = true;
    this.preferredCanvasWidth = bounds.width;
    this.preferredCanvasHeight = bounds.height;
    this.persistViewport();
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  togglePagesDrawer(): void {
    this.showPagesDrawer = !this.showPagesDrawer;
    if (this.showPagesDrawer) {
      this.showLayersDrawer = false;
      this.closeSectionModal();
      this.closeComponentModal();
      this.contextMenuService.close();
    }
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  closePagesDrawer(): void {
    this.showPagesDrawer = false;
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  toggleLayersDrawer(): void {
    this.showLayersDrawer = !this.showLayersDrawer;
    if (this.showLayersDrawer) {
      this.showPagesDrawer = false;
      this.closeSectionModal();
      this.closeComponentModal();
      this.contextMenuService.close();
      this.expandLayerPath(this.websiteBuilderService.selectedNodeId() ?? this.activeDocument?.root.id ?? null);
    }
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  closeLayersDrawer(): void {
    this.showLayersDrawer = false;
    queueMicrotask(() => {
      this.clampCanvasViewport();
      this.refreshCanvasViewportPresentation();
    });
  }

  openSectionModal(): void {
    this.showPagesDrawer = false;
    this.showLayersDrawer = false;
    this.showComponentModal = false;
    this.closeThemeManagerModal();
    this.closePreviewModal();
    this.showSectionModal = true;
    this.contextMenuService.close();
  }

  closeSectionModal(): void {
    this.showSectionModal = false;
  }

  openComponentModal(): void {
    this.showPagesDrawer = false;
    this.showLayersDrawer = false;
    this.showSectionModal = false;
    this.closeThemeManagerModal();
    this.closePreviewModal();
    this.showComponentModal = true;
    this.contextMenuService.close();
  }

  closeComponentModal(): void {
    this.showComponentModal = false;
  }

  openThemeManagerModal(): void {
    this.showPagesDrawer = false;
    this.showLayersDrawer = false;
    this.showSectionModal = false;
    this.showComponentModal = false;
    this.closePreviewModal();
    this.showThemeManagerModal = true;
    this.contextMenuService.close();
    this.loadThemeDraft(this.currentCanvasThemePackageId);
  }

  closeThemeManagerModal(): void {
    this.showThemeManagerModal = false;
  }

  openPreviewModal(): void {
    this.showPagesDrawer = false;
    this.showLayersDrawer = false;
    this.showSectionModal = false;
    this.showComponentModal = false;
    this.closeThemeManagerModal();
    this.showPreviewModal = true;
    this.contextMenuService.close();
    this.initializePreview();
  }

  closePreviewModal(): void {
    this.showPreviewModal = false;
    this.isPreviewLoading = false;
    this.previewError = '';
  }

  async flushPendingChanges(): Promise<void> {
    await Promise.all([
      this.websiteBuilderService.flushPendingSave(),
      this.canvasThemeService.flushPendingSave()
    ]);
  }

  selectCanvasThemePackage(packageId: string): void {
    this.canvasThemeService.setSelectedPackage(packageId);
  }

  setCanvasThemeMode(mode: CanvasThemeMode): void {
    this.canvasThemeService.setPreviewMode(mode);
    this.refreshCanvasViewportPresentation();
  }

  chooseThemeDraft(packageId: string): void {
    this.loadThemeDraft(packageId);
  }

  createThemePackage(): void {
    const createdPackage = this.canvasThemeService.createPackage();
    this.editingThemePackageId = createdPackage.id;
    this.themeDraft = createdPackage;
  }

  saveThemePackage(): void {
    if (!this.themeDraft) {
      return;
    }

    this.canvasThemeService.savePackage(this.themeDraft);
    this.canvasThemeService.setSelectedPackage(this.themeDraft.id);
    this.loadThemeDraft(this.themeDraft.id);
  }

  deleteThemePackage(): void {
    if (!this.themeDraft || this.themeDraft.id === 'default') {
      return;
    }

    this.canvasThemeService.deletePackage(this.themeDraft.id);
    this.loadThemeDraft(this.canvasThemeService.selectedPackageId());
  }

  updateThemeDraftMeta(field: 'name' | 'description', value: string): void {
    if (!this.themeDraft || this.themeDraft.id === 'default') {
      return;
    }

    this.themeDraft = {
      ...this.themeDraft,
      [field]: value
    };
  }

  updateThemeDraftOverride(mode: CanvasThemeMode, key: keyof NonNullable<CanvasThemePackage['lightOverrides']>, value: string): void {
    if (!this.themeDraft || this.themeDraft.id === 'default') {
      return;
    }

    const normalizedValue = value.trim();
    const targetOverrides = {
      ...(mode === 'dark' ? this.themeDraft.darkOverrides : this.themeDraft.lightOverrides)
    };

    if (normalizedValue) {
      targetOverrides[key] = normalizedValue;
    } else {
      delete targetOverrides[key];
    }

    this.themeDraft = {
      ...this.themeDraft,
      ...(mode === 'dark'
        ? { darkOverrides: targetOverrides }
        : { lightOverrides: targetOverrides })
    };
  }

  insertSectionPreset(presetId: string, targetParentId?: string | null): void {
    this.websiteBuilderService.insertSectionPreset(presetId, targetParentId);
    this.closeSectionModal();
  }

  insertComponent(definitionId: string, targetParentId?: string | null): void {
    const definition = this.componentStudioService.definitions().find((entry) => entry.id === definitionId);
    if (!definition) {
      return;
    }

    this.websiteBuilderService.insertComponentDefinition(definition, targetParentId);
    this.closeComponentModal();
  }

  saveSelectedNodeAsComponent(): void {
    if (this.componentLibraryLoading) {
      this.pageMessage = 'Component library is still loading. Try again in a moment.';
      return;
    }

    if (!this.componentStudioService.isLoaded()) {
      this.pageMessage = 'Component library is not ready yet. Reload it and try again.';
      this.loadComponentLibrary(true);
      return;
    }

    const selectedNode = this.selectedNode;
    if (!selectedNode) {
      return;
    }

    const componentId = this.websiteBuilderService.saveSelectedNodeAsComponent();
    if (componentId) {
      this.pageMessage = `Saved "${selectedNode.label}" as a reusable component.`;
    }
  }

  async saveVersion(): Promise<void> {
    const website = this.activeWebsite;
    if (!website || this.isSavingWebsiteVersion) {
      return;
    }

    this.isSavingWebsiteVersion = true;
    this.pageMessage = '';

    try {
      await this.flushPendingChanges();
      const result = await firstValueFrom(
        this.websiteManagementService.createWebsiteVersion(website.publicId, 'Saved from builder')
      );
      this.runInAngular(() => {
        this.isSavingWebsiteVersion = false;
        this.pageMessage = `Saved website version ${result.version.versionNumber}.`;
      });
    } catch (error) {
      this.runInAngular(() => {
        this.isSavingWebsiteVersion = false;
        this.pageMessage = (error as { message?: string })?.message || 'Could not save a website version.';
      });
    }
  }

  async publishWebsite(): Promise<void> {
    const website = this.activeWebsite;
    if (!website || this.isPublishing) {
      return;
    }

    await this.flushPendingChanges();
    this.isPublishing = true;
    this.pageMessage = '';

    this.websiteManagementService.publishWebsite(website.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.runInAngular(() => {
            this.isPublishing = false;
            const liveHost = buildPublishedWebsiteHost(
              environment.apiUrl,
              result.deployment.subdomain,
              this.authService.getCurrentUserSnapshot()?.nickname
            );
            this.pageMessage = `Deployment v${result.deployment.deploymentNumber} is now live on ${liveHost || result.deployment.subdomain}.`;
            this.loadBuilderData(true);
          });
        },
        error: (error) => {
          this.runInAngular(() => {
            this.isPublishing = false;
            this.pageMessage = error.message || 'The website could not be published.';
          });
        }
      });
  }

  openLiveWebsite(): void {
    const website = this.activeWebsite;
    const page = this.activePage;
    if (!website?.subdomain || !page || typeof window === 'undefined') {
      return;
    }

    const liveUrl = buildPublishedWebsiteUrl(
      environment.apiUrl,
      website.subdomain,
      this.authService.getCurrentUserSnapshot()?.nickname,
      page.path
    );
    window.open(liveUrl, '_blank', 'noopener,noreferrer');
  }

  async selectPage(page: WebsitePageSummary): Promise<void> {
    const website = this.activeWebsite;
    if (!website || page.publicId === this.activePage?.publicId) {
      return;
    }

    await this.flushPendingChanges();
    this.router.navigate(['/website-builder'], {
      queryParams: {
        website: website.publicId,
        page: page.publicId
      }
    });
  }

  selectPreviewPage(page: WebsitePageSummary): void {
    this.navigatePreviewToPage(page.publicId);
  }

  handlePreviewButtonClick(node: WebsiteBuilderNode): void {
    this.activatePreviewNode(node);
  }

  handlePreviewNodeClick(node: WebsiteBuilderNode, event?: Event): void {
    if (!this.isInteractiveNode(node) || node.type === 'button') {
      return;
    }

    event?.stopPropagation();
    this.activatePreviewNode(node);
  }

  isInteractiveNode(node: WebsiteBuilderNode): boolean {
    return (node.content.linkKind || 'none') !== 'none';
  }

  previewNodeClasses(node: WebsiteBuilderNode): Record<string, boolean> {
    return {
      'is-interactive': this.isInteractiveNode(node),
      'has-hover-lift': (node.content.hoverEffect || 'none') === 'lift',
      'has-hover-grow': (node.content.hoverEffect || 'none') === 'grow',
      'has-hover-glow': (node.content.hoverEffect || 'none') === 'glow'
    };
  }

  selectNode(nodeId: string, event?: Event): void {
    event?.stopPropagation();
    if (event?.type === 'click' && Date.now() < this.suppressSelectionUntil) {
      event.preventDefault();
      return;
    }

    this.contextMenuService.close();
    this.websiteBuilderService.selectNode(nodeId);
    this.expandLayerPath(nodeId);
  }

  selectLayerNode(nodeId: string, event?: Event): void {
    this.selectNode(nodeId, event);
    this.scrollCanvasNodeIntoView(nodeId);
  }

  handleNodeDrop(event: CdkDragDrop<WebsiteBuilderNode[]>, targetParentId: string): void {
    const nodeId = event.item.data as string;
    this.websiteBuilderService.moveNode(nodeId, targetParentId, event.currentIndex);
    this.websiteBuilderService.selectNode(nodeId);
  }

  handleNodeSurfacePointerDown(event: PointerEvent, node: WebsiteBuilderNode): void {
    if (event.button !== 0 || node.locked || node.nodeRole === 'page-root') {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target || target.closest('.website-builder__resize-handle')) {
      return;
    }

    const closestNodeId = target.closest<HTMLElement>('[data-node-id]')?.dataset['nodeId'] ?? null;
    if (closestNodeId !== node.id) {
      return;
    }

    if (this.builderService.selectedNodeId() !== node.id) {
      this.selectNode(node.id, event);
      return;
    }

    if (target.closest('input, textarea, select, option')) {
      return;
    }

    this.startNodeMove(event, node);
  }

  startNodeMove(event: PointerEvent, node: WebsiteBuilderNode): void {
    event.preventDefault();
    event.stopPropagation();

    const surfaceElement = this.getNodeSurfaceElement(node.id);
    const rootElement = this.getCanvasRootElement();
    if (!surfaceElement) {
      return;
    }
    if (!rootElement) {
      return;
    }

    const rect = surfaceElement.getBoundingClientRect();
    const rootRect = rootElement.getBoundingClientRect();
    const parentRect = this.getParentSurfaceElement(node.id)?.getBoundingClientRect() ?? rootRect;
    const scale = this.canvasScale || 1;
    const parentWidth = parentRect.width / scale;
    const parentHeight = parentRect.height / scale;
    const startOffsetX = parseResponsiveOffset(node.style.left, parentWidth) + node.style.transform.translateX;
    const startOffsetY = parseResponsiveOffset(node.style.top, parentHeight) + node.style.transform.translateY;
    this.websiteBuilderService.selectNode(node.id);
    this.websiteBuilderService.beginHistoryTransaction();
    if (node.style.position === 'static') {
      this.websiteBuilderService.updateSelectedNodeStyle({ position: 'relative' }, false);
    }
    this.nodeManipulationState = {
      kind: 'move',
      nodeId: node.id,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width / scale,
      startHeight: rect.height / scale,
      startOffsetX,
      startOffsetY,
      startCenterX: ((rect.left - parentRect.left) / scale) + ((rect.width / scale) / 2),
      startCenterY: ((rect.top - parentRect.top) / scale) + ((rect.height / scale) / 2),
      parentWidth,
      parentHeight
    };
  }

  startNodeResize(event: PointerEvent, node: WebsiteBuilderNode, direction: NodeResizeDirection): void {
    event.preventDefault();
    event.stopPropagation();

    const surfaceElement = this.getNodeSurfaceElement(node.id);
    const rootElement = this.getCanvasRootElement();
    if (!surfaceElement) {
      return;
    }
    if (!rootElement) {
      return;
    }

    const rect = surfaceElement.getBoundingClientRect();
    const rootRect = rootElement.getBoundingClientRect();
    const parentRect = this.getParentSurfaceElement(node.id)?.getBoundingClientRect() ?? rootRect;
    const scale = this.canvasScale || 1;
    const parentWidth = parentRect.width / scale;
    const parentHeight = parentRect.height / scale;
    const startOffsetX = parseResponsiveOffset(node.style.left, parentWidth) + node.style.transform.translateX;
    const startOffsetY = parseResponsiveOffset(node.style.top, parentHeight) + node.style.transform.translateY;
    this.websiteBuilderService.selectNode(node.id);
    this.websiteBuilderService.beginHistoryTransaction();
    if (node.style.position === 'static') {
      this.websiteBuilderService.updateSelectedNodeStyle({ position: 'relative' }, false);
    }
    this.nodeManipulationState = {
      kind: 'resize',
      nodeId: node.id,
      direction,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width / scale,
      startHeight: rect.height / scale,
      startOffsetX,
      startOffsetY,
      startCenterX: ((rect.left - parentRect.left) / scale) + ((rect.width / scale) / 2),
      startCenterY: ((rect.top - parentRect.top) / scale) + ((rect.height / scale) / 2),
      parentWidth,
      parentHeight
    };
  }

  nodeStyles(node: WebsiteBuilderNode): Record<string, string | number> {
    const styles = this.websiteBuilderService.buildNodeStyles(
      node,
      this.currentCanvasThemePackageId,
      this.currentCanvasThemeMode
    );
    mapCanvasThemeDefaults(styles);

    return styles;
  }

  resolveMediaUrl(value: string | undefined): string {
    if (!value || !value.startsWith('/')) {
      return value || '';
    }

    return new URL(value, environment.apiUrl).toString();
  }

  nodeIcon(nodeType: WebsiteBuilderNodeType): string {
    return getWebsiteBuilderNodeIcon(nodeType);
  }

  isContainer(node: WebsiteBuilderNode): boolean {
    return isWebsiteBuilderContainerNode(node);
  }

  isFluidWrapperNode(node: WebsiteBuilderNode): boolean {
    return node.type !== 'button' && node.type !== 'badge';
  }

  dropListId(nodeId: string): string {
    return websiteBuilderDropListId(nodeId);
  }

  layerPadding(depth: number): string {
    return `${0.35 + depth * 0.72}rem`;
  }

  togglePagesPanelSection(section: 'current' | 'all'): void {
    this.pagesPanelSections[section] = !this.pagesPanelSections[section];
  }

  toggleLayersPanelSection(section: 'selection' | 'tree'): void {
    this.layersPanelSections[section] = !this.layersPanelSections[section];
  }

  hasLayerChildren(node: WebsiteBuilderNode): boolean {
    return node.children.length > 0;
  }

  isLayerExpanded(nodeId: string): boolean {
    return !this.collapsedLayerNodeIds.has(nodeId);
  }

  toggleLayerExpansion(nodeId: string, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.collapsedLayerNodeIds.has(nodeId)) {
      this.collapsedLayerNodeIds.delete(nodeId);
      return;
    }

    this.collapsedLayerNodeIds.add(nodeId);
  }

  openHeaderContextMenu(event: MouseEvent): void {
    if (this.hasNativeContextTarget(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.contextMenuService.openAt(event, [
      {
        id: 'open-pages',
        label: 'Open pages panel',
        icon: 'document-text-outline',
        action: () => this.togglePagesDrawer()
      },
      {
        id: 'open-layers',
        label: 'Open layers panel',
        icon: 'layers-outline',
        action: () => this.toggleLayersDrawer()
      },
      {
        id: 'insert-section',
        label: 'Insert section',
        icon: 'sparkles-outline',
        action: () => this.openSectionModal()
      },
      {
        id: 'insert-component',
        label: 'Insert component',
        icon: 'cube-outline',
        action: () => this.openComponentModal()
      },
      {
        id: 'save-version',
        label: 'Save website version',
        icon: 'save-outline',
        action: () => this.saveVersion()
      },
      {
        id: 'open-preview',
        label: 'Open preview',
        icon: 'eye-outline',
        action: () => this.openPreviewModal()
      },
      {
        id: 'open-themes',
        label: 'Manage canvas themes',
        icon: 'color-palette-outline',
        action: () => this.openThemeManagerModal()
      },
      {
        id: 'save-as-component',
        label: 'Save selection as component',
        icon: 'copy-outline',
        disabled: !this.websiteBuilderService.canSaveSelectedAsComponent(this.selectedNode),
        action: () => this.saveSelectedNodeAsComponent()
      }
    ], 'Website builder actions');
  }

  openWorkspaceContextMenu(event: MouseEvent): void {
    if (this.hasNativeContextTarget(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.contextMenuService.openAt(event, [
      {
        id: 'insert-section',
        label: 'Insert section preset',
        icon: 'sparkles-outline',
        action: () => this.openSectionModal()
      },
      {
        id: 'insert-component',
        label: 'Insert component',
        icon: 'cube-outline',
        action: () => this.openComponentModal()
      },
      {
        id: 'open-pages',
        label: 'Show pages panel',
        icon: 'document-text-outline',
        action: () => this.togglePagesDrawer()
      },
      {
        id: 'open-layers',
        label: 'Show layers panel',
        icon: 'layers-outline',
        action: () => this.toggleLayersDrawer()
      },
      {
        id: 'workspace-themes',
        label: 'Manage canvas themes',
        icon: 'color-palette-outline',
        action: () => this.openThemeManagerModal()
      },
      {
        id: 'workspace-preview',
        label: 'Open preview',
        icon: 'eye-outline',
        action: () => this.openPreviewModal()
      }
    ], 'Canvas actions');
  }

  openCanvasContextMenu(event: MouseEvent, node: WebsiteBuilderNode): void {
    event.preventDefault();
    event.stopPropagation();
    this.websiteBuilderService.selectNode(node.id);
    this.contextMenuService.openAt(event, this.buildNodeContextMenuItems(node), node.label);
  }

  openLayerContextMenu(event: MouseEvent, node: WebsiteBuilderNode): void {
    event.preventDefault();
    event.stopPropagation();
    this.websiteBuilderService.selectNode(node.id);
    this.contextMenuService.openAt(event, this.buildNodeContextMenuItems(node), `${node.label} layer`);
  }

  openPageContextMenu(event: MouseEvent, page: WebsitePageSummary): void {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuService.openAt(event, [
      {
        id: 'open-page',
        label: `Open ${page.name}`,
        icon: 'chevron-forward-outline',
        action: () => this.selectPage(page)
      }
    ], `${page.name} page`);
  }

  openComponentCardContextMenu(event: MouseEvent, definitionId: string): void {
    event.preventDefault();
    event.stopPropagation();

    const definition = this.componentStudioService.definitions().find((entry) => entry.id === definitionId);
    if (!definition) {
      return;
    }

    this.contextMenuService.openAt(event, [
      {
        id: 'insert-component',
        label: `Insert ${definition.name}`,
        icon: 'cube-outline',
        action: () => this.insertComponent(definition.id)
      },
      {
        id: 'edit-component',
        label: 'Open component editor',
        icon: 'copy-outline',
        action: () => this.router.navigate(['/component-builder'], { queryParams: { component: definition.id } })
      }
    ], definition.name);
  }

  resolveInternalTargetLabel(node: WebsiteBuilderNode): string {
    const targetPagePublicId = node.content.targetPagePublicId;
    if (!targetPagePublicId) {
      return 'No internal target';
    }

    const targetPage = this.websiteBuilderService.pages().find((page) => page.publicId === targetPagePublicId);
    return targetPage ? `${targetPage.name} (${targetPage.path})` : 'Linked page';
  }

  isPreviewPage(page: WebsitePageSummary): boolean {
    return this.currentPreviewPage?.publicId === page.publicId;
  }

  startViewportResize(event: PointerEvent, direction: ViewportResizeDirection = 'se'): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.detail > 1) {
      if (direction === 's') {
        this.maximizeViewportHeight();
        return;
      }

      if (direction === 'e' || direction === 'w') {
        this.maximizeViewportWidth();
        return;
      }
    }

    this.resizingViewport = true;
    this.viewportResizeDirection = direction;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.canvasWidth;
    this.resizeStartHeight = this.canvasHeight;
    this.resizeStartScale = this.canvasScale || 1;
    this.refreshCanvasViewportPresentation();
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent): void {
    if (this.nodeManipulationState) {
      event.preventDefault();
      this.updateNodeManipulation(event);
      return;
    }

    if (!this.resizingViewport) {
      return;
    }

    const widthDelta = (event.clientX - this.resizeStartX) / this.resizeStartScale;
    const heightDelta = (event.clientY - this.resizeStartY) / this.resizeStartScale;
    let nextWidth = this.resizeStartWidth;
    let nextHeight = this.resizeStartHeight;
    const bounds = this.getCanvasResizeBounds();

    if (this.viewportResizeDirection.includes('e')) {
      nextWidth += widthDelta * 2;
    }

    if (this.viewportResizeDirection.includes('w')) {
      nextWidth -= widthDelta * 2;
    }

    if (this.viewportResizeDirection.includes('s')) {
      nextHeight += heightDelta;
    }

    this.previewMode = 'responsive';
    this.autoFitPresetZoom = false;
    if (this.viewportResizeDirection.includes('e') || this.viewportResizeDirection.includes('w')) {
      this.canvasWidthFollowsAvailableSpace = false;
    }
    if (this.viewportResizeDirection.includes('s')) {
      this.canvasHeightFollowsAvailableSpace = false;
    }
    this.preferredCanvasWidth = clamp(Math.round(nextWidth), this.minCanvasWidth, this.maxCanvasWidth);
    this.preferredCanvasHeight = clamp(Math.round(nextHeight), this.minCanvasHeight, this.maxCanvasHeight);
    this.canvasWidth = clamp(this.preferredCanvasWidth, this.minCanvasWidth, bounds.width);
    this.canvasHeight = clamp(this.preferredCanvasHeight, this.minCanvasHeight, bounds.height);
    this.refreshCanvasViewportPresentation();
  }

  @HostListener('document:pointerup')
  handlePointerUp(): void {
    const wasResizingViewport = this.resizingViewport;
    const wasManipulatingNode = !!this.nodeManipulationState;
    this.resizingViewport = false;
    this.nodeManipulationState = null;
    this.clearSnapGuides();
    if (wasManipulatingNode) {
      this.suppressSelectionUntil = Date.now() + 160;
    }

    if (wasResizingViewport) {
      this.refreshCanvasViewportPresentation();
      this.persistViewport();
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  handleDocumentPointerDown(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.website-builder__viewport-combo')) {
      return;
    }

    this.showZoomPresetMenu = false;
  }

  handleViewportZoomFocusOut(event: FocusEvent): void {
    const target = event.relatedTarget as HTMLElement | null;
    if (target?.closest('.website-builder__viewport-combo')) {
      return;
    }

    this.showZoomPresetMenu = false;
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.clampCanvasViewport();
    this.refreshCanvasViewportPresentation();
  }

  @HostListener('window:beforeunload')
  @HostListener('window:pagehide')
  handleBeforeUnload(): void {
    this.websiteBuilderService.flushPendingSaveOnUnload();
    this.canvasThemeService.flushPendingSaveOnUnload();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent): void {
    if (this.shouldIgnoreShortcut(event)) {
      return;
    }

    const hasCommandModifier = event.ctrlKey || event.metaKey;
    if (!hasCommandModifier || event.altKey) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'z' && event.shiftKey) {
      if (this.websiteBuilderService.redo()) {
        event.preventDefault();
      }
      return;
    }

    if (key === 'z') {
      if (this.websiteBuilderService.undo()) {
        event.preventDefault();
      }
      return;
    }

    if (key === 'y' && this.websiteBuilderService.redo()) {
      event.preventDefault();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closePagesDrawer();
    this.closeLayersDrawer();
    this.closeSectionModal();
    this.closeComponentModal();
    this.closeThemeManagerModal();
    this.closePreviewModal();
  }

  trackPage(_index: number, page: WebsitePageSummary): string {
    return page.publicId;
  }

  trackNode(_index: number, node: WebsiteBuilderNode): string {
    return node.id;
  }

  private buildNodeContextMenuItems(node: WebsiteBuilderNode): AppContextMenuItem[] {
    const items: AppContextMenuItem[] = [
      {
        id: 'select-node',
        label: 'Select node',
        icon: 'scan-outline',
        action: () => this.websiteBuilderService.selectNode(node.id)
      },
      {
        id: 'duplicate-node',
        label: 'Duplicate node',
        icon: 'copy-outline',
        action: () => this.websiteBuilderService.duplicateSelectedNode()
      },
      {
        id: 'toggle-hidden',
        label: node.hidden ? 'Show node' : 'Hide node',
        icon: 'scan-outline',
        action: () => this.websiteBuilderService.toggleSelectedHidden()
      },
      {
        id: 'toggle-locked',
        label: node.locked ? 'Unlock node' : 'Lock node',
        icon: 'lock-closed-outline',
        action: () => this.websiteBuilderService.toggleSelectedLocked()
      },
      {
        id: 'save-as-component',
        label: 'Save as component',
        icon: 'cube-outline',
        disabled: !this.websiteBuilderService.canSaveSelectedAsComponent(node),
        action: () => this.saveSelectedNodeAsComponent()
      },
      {
        id: 'delete-node',
        label: 'Delete node',
        icon: 'close-outline',
        tone: 'danger',
        disabled: !this.websiteBuilderService.canRemoveSelected(node),
        action: () => this.websiteBuilderService.removeSelectedNode()
      }
    ];

    if (this.isContainer(node)) {
      items.splice(1, 0,
        {
          id: 'insert-section-here',
          label: 'Add section here',
          icon: 'sparkles-outline',
          action: () => {
            this.websiteBuilderService.selectNode(node.id);
            this.openSectionModal();
          }
        },
        {
          id: 'insert-component-here',
          label: 'Add component here',
          icon: 'cube-outline',
          action: () => {
            this.websiteBuilderService.selectNode(node.id);
            this.openComponentModal();
          }
        }
      );
    }

    return items;
  }

  private expandLayerPath(nodeId: string | null): void {
    if (!nodeId || !this.activeDocument) {
      return;
    }

    const path = this.findNodePath(this.activeDocument.root, nodeId);
    if (!path) {
      return;
    }

    for (const ancestor of path.slice(0, Math.max(path.length - 1, 1))) {
      this.collapsedLayerNodeIds.delete(ancestor.id);
    }
  }

  private findNodePath(root: WebsiteBuilderNode, nodeId: string, trail: WebsiteBuilderNode[] = []): WebsiteBuilderNode[] | null {
    const nextTrail = [...trail, root];
    if (root.id === nodeId) {
      return nextTrail;
    }

    for (const child of root.children) {
      const match = this.findNodePath(child, nodeId, nextTrail);
      if (match) {
        return match;
      }
    }

    return null;
  }

  private hasNativeContextTarget(event: MouseEvent): boolean {
    return !!(event.target as HTMLElement | null)?.closest('input, textarea, select, button');
  }

  private shouldIgnoreShortcut(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('input, textarea, select, [contenteditable="true"]');
  }

  private updateNodeManipulation(event: PointerEvent): void {
    const interaction = this.nodeManipulationState;
    if (!interaction) {
      return;
    }

    const scale = this.canvasScale || 1;
    const dx = (event.clientX - interaction.startX) / scale;
    const dy = (event.clientY - interaction.startY) / scale;
    this.websiteBuilderService.selectNode(interaction.nodeId);

    if (interaction.kind === 'move') {
      const snappedMove = this.resolveMoveSnapping(
        interaction,
        interaction.startOffsetX + dx,
        interaction.startOffsetY + dy
      );
      this.websiteBuilderService.updateSelectedNodeStyle({
        position: 'relative',
        left: formatParentRelativeOffset(snappedMove.offsetX, interaction.parentWidth),
        top: formatParentRelativeOffset(snappedMove.offsetY, interaction.parentHeight)
      }, false);
      this.websiteBuilderService.updateSelectedNodeTransform({
        translateX: 0,
        translateY: 0
      }, false);
      return;
    }

    this.clearSnapGuides();

    const direction = interaction.direction ?? 'se';
    let nextWidth = interaction.startWidth;
    let nextHeight = interaction.startHeight;
    let nextOffsetX = interaction.startOffsetX;
    let nextOffsetY = interaction.startOffsetY;

    if (direction.includes('e')) {
      nextWidth += dx;
    }

    if (direction.includes('w')) {
      nextWidth -= dx;
      nextOffsetX += dx;
    }

    if (direction.includes('s')) {
      nextHeight += dy;
    }

    if (direction.includes('n')) {
      nextHeight -= dy;
      nextOffsetY += dy;
    }

    const clampedWidth = clamp(nextWidth, 48, 2400);
    const clampedHeight = clamp(nextHeight, 36, 2400);
    if (direction.includes('w')) {
      nextOffsetX = interaction.startOffsetX + (interaction.startWidth - clampedWidth);
    }
    if (direction.includes('n')) {
      nextOffsetY = interaction.startOffsetY + (interaction.startHeight - clampedHeight);
    }

    const widthValue = interaction.parentWidth > 0
      ? `${roundTo(clamp((clampedWidth / interaction.parentWidth) * 100, 3, 100), 2)}%`
      : `${Math.round(clampedWidth)}px`;
    const aspectRatioValue = clampedHeight > 0
      ? `${roundTo(clampedWidth / clampedHeight, 3)}`
      : 'auto';

    this.websiteBuilderService.updateSelectedNodeStyle({
      width: widthValue,
      height: 'auto',
      aspectRatio: aspectRatioValue,
      maxWidth: '100%',
      minHeight: 'auto',
      position: 'relative',
      left: formatParentRelativeOffset(nextOffsetX, interaction.parentWidth),
      top: formatParentRelativeOffset(nextOffsetY, interaction.parentHeight)
    }, false);

    this.websiteBuilderService.updateSelectedNodeTransform({
      translateX: 0,
      translateY: 0
    }, false);
  }

  private getNodeSurfaceElement(nodeId: string): HTMLElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector(
      `.website-builder__node[data-node-id="${nodeId}"] > .website-builder__node-surface`
    ) ?? null;
  }

  private getParentSurfaceElement(nodeId: string): HTMLElement | null {
    const surfaceElement = this.getNodeSurfaceElement(nodeId);
    const nodeElement = surfaceElement?.closest<HTMLElement>('.website-builder__node[data-node-id]');
    const parentNodeElement = nodeElement?.parentElement?.closest<HTMLElement>('.website-builder__node[data-node-id]');
    if (!parentNodeElement) {
      return this.getCanvasRootElement();
    }

    return Array.from(parentNodeElement.children)
      .find((child): child is HTMLElement => child instanceof HTMLElement && child.classList.contains('website-builder__node-surface'))
      ?? this.getCanvasRootElement();
  }

  private scrollCanvasNodeIntoView(nodeId: string): void {
    queueMicrotask(() => {
      const viewport = this.getCanvasViewportElement();
      const target = nodeId === this.activeDocument?.root.id
        ? this.getCanvasRootElement()
        : this.getNodeSurfaceElement(nodeId);

      if (!viewport || !target) {
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const edgePadding = 24;
      let nextScrollLeft = viewport.scrollLeft;
      let nextScrollTop = viewport.scrollTop;

      if (targetRect.left < viewportRect.left + edgePadding) {
        nextScrollLeft += targetRect.left - (viewportRect.left + edgePadding);
      } else if (targetRect.right > viewportRect.right - edgePadding) {
        nextScrollLeft += targetRect.right - (viewportRect.right - edgePadding);
      }

      if (targetRect.top < viewportRect.top + edgePadding) {
        nextScrollTop += targetRect.top - (viewportRect.top + edgePadding);
      } else if (targetRect.bottom > viewportRect.bottom - edgePadding) {
        nextScrollTop += targetRect.bottom - (viewportRect.bottom - edgePadding);
      }

      nextScrollLeft = clamp(Math.round(nextScrollLeft), 0, Math.max(0, viewport.scrollWidth - viewport.clientWidth));
      nextScrollTop = clamp(Math.round(nextScrollTop), 0, Math.max(0, viewport.scrollHeight - viewport.clientHeight));

      viewport.scrollTo({
        left: nextScrollLeft,
        top: nextScrollTop,
        behavior: 'smooth'
      });
    });
  }

  private getCanvasRootElement(): HTMLElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector('.website-builder__canvas-root') ?? null;
  }

  private getCanvasViewportElement(): HTMLElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector('.website-builder__canvas-viewport') ?? null;
  }

  private getGridCanvasElement(): HTMLCanvasElement | null {
    return this.canvasViewportShellRef?.nativeElement.querySelector('.website-builder__viewport-grid') ?? null;
  }

  private initializePreview(): void {
    const activePage = this.activePage;
    const activeDocument = this.activeDocument;

    this.previewError = '';

    if (!activePage || !activeDocument) {
      this.previewPage = null;
      this.previewDocument = null;
      return;
    }

    this.previewPage = activePage;
    this.previewDocument = structuredClone(activeDocument);
    this.previewCache.set(activePage.publicId, {
      page: activePage,
      document: structuredClone(activeDocument)
    });
  }

  private activatePreviewNode(node: WebsiteBuilderNode): void {
    const linkKind = node.content.linkKind || 'none';

    if (linkKind === 'internal-page' && node.content.targetPagePublicId) {
      this.navigatePreviewToPage(node.content.targetPagePublicId);
      return;
    }

    if (linkKind === 'external-url' && node.content.href && typeof window !== 'undefined') {
      window.open(node.content.href, '_blank', 'noopener,noreferrer');
    }
  }

  private navigatePreviewToPage(pagePublicId: string): void {
    const website = this.activeWebsite;
    if (!website) {
      return;
    }

    const targetPage = this.previewPages.find((page) => page.publicId === pagePublicId);
    if (!targetPage) {
      this.previewError = 'The linked page could not be found in this website.';
      return;
    }

    if (targetPage.publicId === this.activePage?.publicId) {
      this.previewPage = targetPage;
      this.previewDocument = this.activeDocument ? structuredClone(this.activeDocument) : null;
      this.previewError = '';
      this.isPreviewLoading = false;
      return;
    }

    const cached = this.previewCache.get(targetPage.publicId);
    if (cached) {
      this.previewPage = cached.page;
      this.previewDocument = structuredClone(cached.document);
      this.previewError = '';
      this.isPreviewLoading = false;
      return;
    }

    const cycle = ++this.previewLoadCycle;
    this.isPreviewLoading = true;
    this.previewError = '';

    this.websiteBuilderApiService.getDraft(website.publicId, targetPage.publicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            if (cycle !== this.previewLoadCycle) {
              return;
            }

            this.previewPage = state.page;
            this.previewDocument = state.document;
            this.previewCache.set(state.page.publicId, {
              page: state.page,
              document: structuredClone(state.document)
            });
            this.isPreviewLoading = false;
          });
        },
        error: () => {
          this.runInAngular(() => {
            if (cycle !== this.previewLoadCycle) {
              return;
            }

            this.isPreviewLoading = false;
            this.previewError = 'The linked page draft could not be loaded for preview.';
          });
        }
      });
  }

  private parsePublicId(value: string | null): string | null {
    if (!value) {
      return null;
    }

    if (!isUuidV7(value)) {
      return null;
    }

    return value;
  }

  private restoreStoredViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const storedMode = window.localStorage.getItem(`${this.canvasViewportStorageKey}.mode`);
    const storedWidth = Number(window.localStorage.getItem(`${this.canvasViewportStorageKey}.width`));
    const storedHeight = Number(window.localStorage.getItem(`${this.canvasViewportStorageKey}.height`));
    const storedZoom = Number(window.localStorage.getItem(`${this.canvasViewportStorageKey}.zoom`));

    if (
      storedMode === 'responsive'
      || storedMode === 'mobile-s'
      || storedMode === 'mobile-m'
      || storedMode === 'mobile-l'
      || storedMode === 'tablet'
      || storedMode === 'laptop'
      || storedMode === 'laptop-lg'
      || storedMode === 'desktop'
      || storedMode === '4k'
    ) {
      this.previewMode = storedMode;
      this.autoFitPresetZoom = storedMode !== 'responsive';
    }

    if (!Number.isNaN(storedWidth) && storedWidth > 0) {
      this.preferredCanvasWidth = clamp(storedWidth, this.minCanvasWidth, this.maxCanvasWidth);
      this.canvasWidth = this.preferredCanvasWidth;
    }

    if (!Number.isNaN(storedHeight) && storedHeight > 0) {
      this.preferredCanvasHeight = clamp(storedHeight, this.minCanvasHeight, this.maxCanvasHeight);
      this.canvasHeight = this.preferredCanvasHeight;
    }

    if (!Number.isNaN(storedZoom) && storedZoom > 0) {
      this.canvasZoomPercentage = clamp(Math.round(storedZoom), 5, 300);
    }

    this.canvasWidthFollowsAvailableSpace = window.localStorage.getItem(`${this.canvasViewportStorageKey}.fitWidth`) === '1';
    this.canvasHeightFollowsAvailableSpace = window.localStorage.getItem(`${this.canvasViewportStorageKey}.fitHeight`) === '1';
    this.showViewportGrid = window.localStorage.getItem(`${this.canvasViewportStorageKey}.grid`) === '1';
    this.showViewportRulers = window.localStorage.getItem(`${this.canvasViewportStorageKey}.rulers`) === '1';
  }

  private persistViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(`${this.canvasViewportStorageKey}.mode`, this.previewMode);
    window.localStorage.setItem(`${this.canvasViewportStorageKey}.width`, String(this.preferredCanvasWidth));
    window.localStorage.setItem(`${this.canvasViewportStorageKey}.height`, String(this.preferredCanvasHeight));
    window.localStorage.setItem(`${this.canvasViewportStorageKey}.zoom`, String(this.canvasZoomPercentage));
    window.localStorage.setItem(`${this.canvasViewportStorageKey}.fitWidth`, this.canvasWidthFollowsAvailableSpace ? '1' : '0');
    window.localStorage.setItem(`${this.canvasViewportStorageKey}.fitHeight`, this.canvasHeightFollowsAvailableSpace ? '1' : '0');
    window.localStorage.setItem(`${this.canvasViewportStorageKey}.grid`, this.showViewportGrid ? '1' : '0');
    window.localStorage.setItem(`${this.canvasViewportStorageKey}.rulers`, this.showViewportRulers ? '1' : '0');
  }

  private clampCanvasViewport(): void {
    this.syncPresetZoomToAvailableWidth();
    const bounds = this.getCanvasResizeBounds();
    if (this.canvasWidthFollowsAvailableSpace) {
      this.preferredCanvasWidth = bounds.width;
    }
    if (this.canvasHeightFollowsAvailableSpace) {
      this.preferredCanvasHeight = bounds.height;
    }
    this.canvasWidth = clamp(this.preferredCanvasWidth, this.minCanvasWidth, this.maxCanvasWidth);
    this.canvasHeight = clamp(this.preferredCanvasHeight, this.minCanvasHeight, bounds.height);
  }

  private setupCanvasViewportObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return;
    }

    this.canvasViewportResizeObserver?.disconnect();
    this.canvasViewportResizeObserver = new ResizeObserver(() => {
      queueMicrotask(() => {
        this.runInAngular(() => {
          this.clampCanvasViewport();
          this.refreshCanvasViewportPresentation();
        });
      });
    });
    this.canvasViewportResizeObserver.observe(shellElement);
  }

  private refreshCanvasViewportPresentation(): void {
    const { width: availableWidth } = this.getCanvasAvailableSpace();
    const frameChrome = this.getCanvasViewportFrameChromePx();
    const rulerReserve = this.getCanvasViewportRulerReservePx();
    const effectiveWidthSpace = Math.max(1, availableWidth - frameChrome.width - rulerReserve.width);
    const widthFitScale = effectiveWidthSpace / this.canvasWidth;
    const requestedScale = clamp(this.canvasZoomPercentage / 100, 0.05, 3);
    // Keep width and height constraints independent:
    // width pressure should affect width scaling, while height pressure should
    // clamp the logical viewport height instead of shrinking width too.
    const effectiveScale = clamp(Math.min(widthFitScale, requestedScale), 0.05, 3);
    this.canvasFitScale = clamp(widthFitScale, 0.05, 3);
    this.canvasScale = effectiveScale;
    this.canvasViewportStyle = {
      'color-scheme': this.currentCanvasThemeMode,
      '--bw-viewport-scrollbar-width': `${this.canvasViewportScrollbarWidth}px`,
      width: `${this.canvasWidth}px`,
      height: `${this.canvasHeight}px`
    };
    this.canvasViewportFrameStyle = {
      '--bw-ruler-thickness': `${rulerReserve.width}px`,
      width: `${this.canvasWidth * effectiveScale + this.remToPx(this.canvasViewportFrameChromeWidthRem) + rulerReserve.width}px`,
      height: `${this.canvasHeight * effectiveScale + this.remToPx(this.canvasViewportFrameChromeHeightRem) + rulerReserve.height}px`,
      transform: 'translateX(0)'
    };
    this.scaledCanvasViewportStyle = {
      ...this.canvasViewportStyle,
      transform: `scale(${effectiveScale})`,
      'transform-origin': 'top left'
    };
    this.refreshCanvasChromePresentation();
    this.scheduleCanvasViewportScrollbarMeasurement();
  }

  private refreshCanvasChromePresentation(): void {
    const viewportElement = this.getCanvasViewportElement();
    const gridWidth = viewportElement?.clientWidth ?? this.canvasWidth;
    const gridHeight = viewportElement?.clientHeight ?? this.canvasHeight;
    this.gridStyle = {
      width: `${gridWidth}px`,
      height: `${gridHeight}px`,
      transform: `scale(${this.canvasScale})`,
      'transform-origin': 'top left'
    };
    this.topRulerCanvasStyle = {
      width: `${gridWidth * this.canvasScale}px`
    };
    this.leftRulerCanvasStyle = {
      height: `${Math.max(1, gridHeight * this.canvasScale)}px`
    };
    if (this.showViewportGrid) {
      this.adaptiveGridRenderer.refreshObservation();
      this.adaptiveGridRenderer.scheduleRender();
    } else {
      this.adaptiveGridRenderer.disconnect();
    }
    if (this.showViewportRulers) {
      this.viewportRulerRenderer.scheduleRender();
    } else {
      this.viewportRulerRenderer.disconnect();
    }
  }

  private getCanvasResizeBounds(): { width: number; height: number } {
    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return {
        width: this.maxCanvasWidth,
        height: this.maxCanvasHeight
      };
    }

    const requestedScale = clamp(this.canvasZoomPercentage / 100, 0.05, 3);
    const { width: availableWidth, height: availableHeight } = this.getCanvasAvailableSpace();
    const frameChrome = this.getCanvasViewportFrameChromePx();
    const rulerReserve = this.getCanvasViewportRulerReservePx();

    return {
      width: clamp(Math.floor(Math.max(1, availableWidth - frameChrome.width - rulerReserve.width) / requestedScale), this.minCanvasWidth, this.maxCanvasWidth),
      height: clamp(Math.floor(Math.max(1, availableHeight - frameChrome.height - rulerReserve.height) / requestedScale), this.minCanvasHeight, this.maxCanvasHeight)
    };
  }

  private getCanvasAvailableSpace(): { width: number; height: number } {
    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return {
        width: this.canvasWidth,
        height: this.canvasHeight
      };
    }

    const shellRect = shellElement.getBoundingClientRect();
    const baseWidth = shellElement.clientWidth || Math.round(shellRect.width) || this.canvasWidth;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : Math.round(shellRect.height);
    const baseHeight = Math.max(1, viewportHeight - shellRect.top);

    return {
      width: Math.max(1, baseWidth - this.canvasViewportFitPaddingX),
      height: Math.max(1, baseHeight - this.canvasViewportFitPaddingY)
    };
  }

  private syncPresetZoomToAvailableWidth(): void {
    if (this.previewMode === 'responsive' || !this.autoFitPresetZoom) {
      return;
    }

    const shellElement = this.canvasViewportShellRef?.nativeElement;
    if (!shellElement) {
      return;
    }

    const { width: availableWidth } = this.getCanvasAvailableSpace();
    const frameChrome = this.getCanvasViewportFrameChromePx();
    const rulerReserve = this.getCanvasViewportRulerReservePx();
    const effectiveWidthSpace = Math.max(1, availableWidth - frameChrome.width - rulerReserve.width);
    const fitZoomPercentage = clamp(Math.floor((effectiveWidthSpace / Math.max(1, this.preferredCanvasWidth)) * 100), 5, 100);
    this.canvasZoomPercentage = fitZoomPercentage;
  }

  private getCanvasViewportFrameChromePx(): { width: number; height: number } {
    return {
      width: this.remToPx(this.canvasViewportFrameChromeWidthRem + this.canvasViewportHandleOverhangWidthRem),
      height: this.remToPx(this.canvasViewportFrameChromeHeightRem + this.canvasViewportHandleOverhangHeightRem)
    };
  }

  private getCanvasViewportRulerReservePx(): { width: number; height: number } {
    if (!this.showViewportRulers) {
      return { width: 0, height: 0 };
    }

    const thickness = this.remToPx(this.canvasViewportRulerThicknessRem);
    return {
      width: thickness,
      height: thickness
    };
  }

  private remToPx(rem: number): number {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return rem * 16;
    }

    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
    return rem * (Number.isFinite(rootFontSize) && rootFontSize > 0 ? rootFontSize : 16);
  }

  private scheduleCanvasViewportScrollbarMeasurement(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      return;
    }

    if (this.canvasViewportScrollbarMeasureFrame !== null) {
      cancelAnimationFrame(this.canvasViewportScrollbarMeasureFrame);
    }

    this.canvasViewportScrollbarMeasureFrame = requestAnimationFrame(() => {
      this.canvasViewportScrollbarMeasureFrame = null;
      const viewportElement = this.getCanvasViewportElement();
      if (!viewportElement) {
        return;
      }

      const nextScrollbarWidth = Math.max(0, Math.round(viewportElement.offsetWidth - viewportElement.clientWidth));
      if (nextScrollbarWidth === this.canvasViewportScrollbarWidth) {
        return;
      }

      this.canvasViewportScrollbarWidth = nextScrollbarWidth;
      this.refreshCanvasViewportPresentation();
      this.changeDetectorRef.detectChanges();
    });
  }

  private resolveMoveSnapping(
    interaction: NodeManipulationState,
    rawOffsetX: number,
    rawOffsetY: number
  ): { offsetX: number; offsetY: number } {
    const threshold = 8;
    const otherCenters = this.collectSiblingNodeCenters(interaction.nodeId);
    const currentCenterX = interaction.startCenterX + (rawOffsetX - interaction.startOffsetX);
    const currentCenterY = interaction.startCenterY + (rawOffsetY - interaction.startOffsetY);

    let snappedOffsetX = rawOffsetX;
    let snappedOffsetY = rawOffsetY;
    let guideX: number | null = null;
    let guideY: number | null = null;

    const xCandidates = [interaction.parentWidth / 2, ...otherCenters.map((entry) => entry.centerX)];
    const yCandidates = [interaction.parentHeight / 2, ...otherCenters.map((entry) => entry.centerY)];

    const closestX = this.closestSnapCandidate(currentCenterX, xCandidates, threshold);
    const closestY = this.closestSnapCandidate(currentCenterY, yCandidates, threshold);

    if (closestX !== null) {
      snappedOffsetX += closestX - currentCenterX;
      guideX = closestX;
    }

    if (closestY !== null) {
      snappedOffsetY += closestY - currentCenterY;
      guideY = closestY;
    }

    this.snapGuideX = guideX;
    this.snapGuideY = guideY;

    return {
      offsetX: snappedOffsetX,
      offsetY: snappedOffsetY
    };
  }

  private collectSiblingNodeCenters(excludedNodeId: string): Array<{ centerX: number; centerY: number }> {
    const parentElement = this.getParentSurfaceElement(excludedNodeId);
    if (!parentElement) {
      return [];
    }

    const scale = this.canvasScale || 1;
    const parentRect = parentElement.getBoundingClientRect();
    const surfaces = Array.from(
      parentElement.querySelectorAll<HTMLElement>(':scope > .website-builder__node[data-node-id] > .website-builder__node-surface')
    );

    return surfaces
      .map((surface) => {
        const nodeId = surface.parentElement?.getAttribute('data-node-id') ?? '';
        if (!nodeId || nodeId === excludedNodeId) {
          return null;
        }

        const rect = surface.getBoundingClientRect();
        return {
          centerX: ((rect.left - parentRect.left) / scale) + ((rect.width / scale) / 2),
          centerY: ((rect.top - parentRect.top) / scale) + ((rect.height / scale) / 2)
        };
      })
      .filter((entry): entry is { centerX: number; centerY: number } => !!entry);
  }

  private closestSnapCandidate(current: number, candidates: number[], threshold: number): number | null {
    let closest: number | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const candidate of candidates) {
      const distance = Math.abs(candidate - current);
      if (distance <= threshold && distance < closestDistance) {
        closest = candidate;
        closestDistance = distance;
      }
    }

    return closest;
  }

  private clearSnapGuides(): void {
    this.snapGuideX = null;
    this.snapGuideY = null;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }

  private getAdaptiveGridContext(): AdaptiveViewportGridContext | null {
    if (!this.showViewportGrid) {
      return null;
    }

    const canvas = this.getGridCanvasElement();
    const viewport = this.getCanvasViewportElement();
    const root = this.getCanvasRootElement();
    if (!canvas || !viewport || !root) {
      return null;
    }

    return {
      canvas,
      viewport,
      root,
      scale: this.canvasScale || 1,
      scrollLeft: this.canvasViewportScrollLeft,
      scrollTop: this.canvasViewportScrollTop,
      themeMode: this.currentCanvasThemeMode as AdaptiveGridThemeMode
    };
  }

  private getViewportRulerContext(): ViewportRulerRendererContext | null {
    if (!this.showViewportRulers) {
      return null;
    }

    const topCanvas = this.canvasViewportShellRef?.nativeElement.querySelector(
      '.website-builder__viewport-ruler-canvas--top'
    ) as HTMLCanvasElement | null;
    const leftCanvas = this.canvasViewportShellRef?.nativeElement.querySelector(
      '.website-builder__viewport-ruler-canvas--left'
    ) as HTMLCanvasElement | null;

    if (!topCanvas || !leftCanvas) {
      return null;
    }

    return {
      topCanvas,
      leftCanvas,
      scale: this.canvasScale || 1,
      scrollLeft: this.canvasViewportScrollLeft,
      scrollTop: this.canvasViewportScrollTop,
      logicalWidth: Math.max(this.canvasWidth, this.getCanvasViewportElement()?.scrollWidth ?? this.canvasWidth),
      logicalHeight: Math.max(this.canvasHeight, this.getCanvasViewportElement()?.scrollHeight ?? this.canvasHeight)
    };
  }

  private scheduleLoadSettlement(cycle: number, callback: () => void): void {
    this.clearLoadSettlementTimer();
    this.loadSettlementTimer = setTimeout(() => {
      if (cycle !== this.loadCycle) {
        return;
      }

      this.runInAngular(callback);
    }, 0);
  }

  private clearLoadSettlementTimer(): void {
    if (!this.loadSettlementTimer) {
      return;
    }

    clearTimeout(this.loadSettlementTimer);
    this.loadSettlementTimer = null;
  }

  private loadThemeDraft(packageId: string): void {
    this.editingThemePackageId = packageId;
    this.themeDraft = this.canvasThemeService.getPackageForEdit(packageId);
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function parseResponsiveOffset(value: string | null | undefined, parentSize: number): number {
  const normalizedValue = (value ?? '').trim();
  if (!normalizedValue || normalizedValue === 'auto') {
    return 0;
  }

  const numericValue = Number.parseFloat(normalizedValue);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  if (normalizedValue.endsWith('%')) {
    return parentSize > 0 ? (numericValue / 100) * parentSize : 0;
  }

  return numericValue;
}

function formatParentRelativeOffset(value: number, parentSize: number): string {
  if (parentSize <= 0) {
    return `${Math.round(value)}px`;
  }

  return `${roundTo((value / parentSize) * 100, 2)}%`;
}
